const colors = {
  grau: '#888',
  ocker: '#bd8d40',
  altweiß: '#f0eee4',
  braun: '#795548',
  feuerrot: '#c5352b',
  flieder: '#b6a0c7',
  gold: '#c8a445',
  goldgelb: '#dfb534',
  königsblau: '#3055a6',
  'matt braun': '#795548',
  'matt nachtblau': '#26334e',
  'matt petrol': '#2c7179',
  'matt schwarz': '#242424',
  'matt weiß': '#fff',
  'matte rose': '#dca5b0',
  rose: '#dca5b0',
  blau: '#355993',
  hellblau: '#98bfdb',
  schwarz: '#242424',
  weiß: '#fff',
  elfenbein: '#e7dfc8',
  rot: '#ad3c32',
  grün: '#557a4e',
  gelb: '#e1bc42',
  orange: '#d58136',
  rosa: '#dca5b0',
  aquamarin: '#75bdb8',
  petrol: '#2c7179',
  lindgrün: '#a4b981',
  perlgrau: '#aaa'
};

const colorAliases = {
  gray: 'grau',
  grey: 'grau',
  ochre: 'ocker',
  "old white": "altweiß",
  "aquamarine": "aquamarin",
  "blue": "blau",
  "brown": "braun",
  "ivory": "elfenbein",
  "fire red": "feuerrot",
  "lilac": "flieder",
  "yellow": "gelb",
  "gold": "gold",
  "golden yellow": "goldgelb",
  "green": "grün",
  "light blue": "hellblau",
  "royal blue": "königsblau",
  "lime green": "lindgrün",
  "dull brown": "matt braun",
  "matt midnight blue": "matt nachtblau",
  "matte petrol": "matt petrol",
  "matt black": "matt schwarz",
  "matte white": "matt weiß",
  "matt rose": "matte rose",
  "orange": "orange",
  "pearl gray": "perlgrau",
  "petrol": "petrol",
  "rose": "rose",
  "red": "rot",
  "black": "schwarz",
  "white": "weiß"
};

class CollectionFilters {
  constructor(root) {
    this.root = root;
    this.translations = JSON.parse(root.querySelector('[data-hb-i18n]').textContent);
    this.events = new AbortController();
    this.renderedUrl = location.href;
    this.previousScrollRestoration = history.scrollRestoration;
    history.scrollRestoration = 'manual';
    this.bind();
    this.root.dataset.enhanced = 'true';
    this.updateSwatches();
    this.updatePriceRanges();
    if (document.readyState === 'complete') this.revealSelectedCategory();
    const url = this.initialUrl();
    const savedPosition = history.state?.hbFiltersScroll;
    if (url.href !== location.href) {
      this.navigate(url, false, false, savedPosition && { ...savedPosition, url: url.href });
    } else {
      this.loadCategoryCounts();
      this.restoreScrollPosition(savedPosition);
    }
  }

  initialUrl() {
    const url = new URL(location.href);
    if (this.root.dataset.searchType) url.searchParams.set('type', this.root.dataset.searchType);
    const legacy = {
      hb_color: 'filter.p.m.my_fields.filter_colors',
      hb_delivery: 'filter.p.m.my_fields.filter_delivery_time',
      hb_available: 'filter.v.availability',
      hb_category: 'filter.p.t.category'
    };
    for (const [oldKey, newKey] of Object.entries(legacy)) {
      if (!url.searchParams.has(newKey)) {
        for (let value of url.searchParams.getAll(oldKey)) {
          if (oldKey === 'hb_color') {
            const normalized = value.toLowerCase();
            const canonical = Object.hasOwn(colorAliases, normalized) ? colorAliases[normalized] : normalized;
            const label = this.translations.colors[canonical] || value;
            const inputs = this.root.querySelectorAll('[data-group="color"] input');
            value = [...inputs].find(input => input.value.toLowerCase() === label.toLowerCase())?.value || value;
          }
          if (oldKey === 'hb_delivery') {
            const match = /^(\d+)[–-](\d+)(?:-|\s+)(days|Tage|días|dias|jours|giorni|weeks|Wochen|semanas|semaines|settimane)$/i.exec(value);
            if (match) {
              const unit = /^(days|Tage|días|dias|jours|giorni)$/i.test(match[3]) ? 'days' : 'weeks';
              const label = this.translations[unit].replace('__range__', `${match[1]}-${match[2]}`);
              const normalize = text => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
              value = [...this.root.querySelectorAll('[data-group="delivery"] input')].find(input => normalize(input.value) === normalize(label))?.value || label;
            }
          }
          url.searchParams.append(newKey, value);
        }
      }
      url.searchParams.delete(oldKey);
    }
    const sort = url.searchParams.get('hb_sort');
    if (sort && sort !== 'default' && !url.searchParams.has('sort_by')) url.searchParams.set('sort_by', sort);
    const page = Number(url.searchParams.get('hb_page'));
    if (Number.isSafeInteger(page) && page > 1 && !url.searchParams.has('page')) url.searchParams.set('page', page);
    url.searchParams.delete('hb_sort');
    url.searchParams.delete('hb_page');
    if (this.root.dataset.decor !== 'true' && this.root.dataset.defaultAvailable === 'true' &&
        !url.searchParams.has('filter.v.availability') && !url.searchParams.has('hb_all')) {
      url.searchParams.set('filter.v.availability', '1');
    }
    return url;
  }

  bind() {
    const options = { signal: this.events.signal };
    this.root.addEventListener('input', event => {
      if (event.target.matches('[data-hb-price-bound]')) this.updatePriceRange(event.target.closest('[data-group="price"]'), event.target);
    }, options);
    this.root.addEventListener('change', event => {
      if (event.target.matches('[data-hb-price-number]')) this.updatePriceRange(event.target.closest('[data-group="price"]'), event.target);
      if (event.target.closest('[data-hb-form]')) this.submit();
    }, options);
    this.root.addEventListener('submit', event => {
      if (!event.target.matches('[data-hb-form]')) return;
      event.preventDefault();
      this.submit();
    }, options);
    this.root.addEventListener('click', event => {
      if (event.target.closest('[data-hb-products] a[href]')) this.saveScrollPosition();
      const link = event.target.closest('.hb-reset, .hb-chip, [data-hb-pages] a, [data-hb-category-link]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const url = link.matches('[data-hb-category-link]') ? this.categoryUrl(link.href) : new URL(link.href);
      if (url.origin !== location.origin) return;
      event.preventDefault();
      if (this.root.dataset.view) url.searchParams.set('view', this.root.dataset.view);
      else url.searchParams.delete('view');
      if (link.matches('.hb-reset') || !url.searchParams.has('filter.v.availability')) url.searchParams.set('hb_all', '1');
      this.navigate(url, true, link.matches('[data-hb-pages] a'));
    }, options);
    document.addEventListener('click', event => {
      for (const details of this.root.querySelectorAll('.hb-facet[open]')) {
        if (!details.contains(event.target)) details.open = false;
      }
    }, options);
    this.root.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      const details = event.target.closest('.hb-facet');
      if (details) { details.open = false; details.querySelector('summary').focus(); }
    }, options);
    window.addEventListener('load', () => this.revealSelectedCategory(), { once: true, signal: this.events.signal });
    window.addEventListener('popstate', event => this.onHistoryChange(event.state), options);
    window.addEventListener('pagehide', () => {
      this.saveScrollPosition();
      this.request?.abort();
      this.countRequest?.abort();
    }, options);
    window.addEventListener('pageshow', event => {
      if (event.persisted) {
        this.onHistoryChange(history.state);
        if (this.renderedUrl === location.href) this.loadCategoryCounts();
      }
    }, options);
  }

  countDelay(ms, signal) {
    return new Promise(resolve => {
      const finish = () => { clearTimeout(timer); signal.removeEventListener('abort', finish); resolve(); };
      const timer = setTimeout(finish, ms);
      signal.addEventListener('abort', finish, { once: true });
      if (signal.aborted) finish();
    });
  }

  async loadCategoryCounts() {
    this.countRequest?.abort();
    const links = [...this.root.querySelectorAll('[data-hb-category-link]')];
    if (!links.length) return;
    const request = this.countRequest = new AbortController();
    this.countCache ||= new Map();
    const currentCount = Number(this.root.querySelector?.('[data-hb-count]')?.dataset.hbResultCount);
    const currentLink = links.find(link => link.getAttribute?.('aria-current') === 'true');
    const format = count => new Intl.NumberFormat(this.root.dataset.locale || undefined).format(count);
    if (currentLink && Number.isSafeInteger(currentCount) && currentCount >= 0) {
      const counter = currentLink.querySelector('[data-hb-category-count]');
      counter.textContent = format(currentCount);
      counter.removeAttribute('aria-busy');
    }
    for (const link of links) {
      if (request.signal.aborted) return;
      const counter = link.querySelector('[data-hb-category-count]');
      if (!counter) continue;
      const url = this.categoryUrl(link.href);
      url.searchParams.set('view', 'hb-sale-count');
      url.searchParams.delete('sections');
      url.searchParams.delete('section_id');
      url.searchParams.delete('sort_by');
      url.searchParams.sort();
      try {
        let count = link === currentLink && Number.isSafeInteger(currentCount) && currentCount >= 0 ? currentCount : this.countCache.get(url.href);
        if (count === undefined) {
          // Give interactive filtering priority over optional facet counts.
          await this.countDelay(500, request.signal);
          if (request.signal.aborted) return;
          let response = await fetch(url, { credentials: 'same-origin', signal: request.signal });
          if (response.status === 429) {
            const seconds = Number(response.headers?.get('Retry-After')) || 2;
            await this.countDelay(Math.min(15000, Math.max(1000, seconds * 1000)), request.signal);
            if (request.signal.aborted) return;
            response = await fetch(url, { credentials: 'same-origin', signal: request.signal });
          }
          if (!response.ok) throw new Error('Category count unavailable');
          const data = await response.json();
          if (!Number.isSafeInteger(data.count) || data.count < 0) throw new Error('Invalid category count');
          count = data.count;
        }
        if (request.signal.aborted) return;
        if (this.countCache.size >= 100) this.countCache.delete(this.countCache.keys().next().value);
        this.countCache.set(url.href, count);
        counter.textContent = format(count);
        this.updateCategoryVisibility(new Map([[link, count]]));
      } catch (error) {
        if (request.signal.aborted) return;
        counter.textContent = '—';
      }
      counter.removeAttribute('aria-busy');
    }
  }

  updateCategoryVisibility(counts) {
    const changes = [];
    for (const [link, count] of counts) {
      const cell = link.closest?.('.subcollections-grid__item');
      if (!cell) continue;
      const hidden = count === 0 && link.dataset.hbCategoryLink !== 'all' && link.getAttribute('aria-current') !== 'true';
      if (cell.hidden !== hidden) changes.push({ cell, hidden });
    }
    if (!changes.length) return;
    // Rebuild through the theme so hidden tiles do not leave empty carousel cells.
    this.updateThemeSection('unload');
    for (const { cell, hidden } of changes) {
      cell.hidden = hidden;
      cell.toggleAttribute('data-subcollections-grid-item', !hidden);
    }
    this.updateThemeSection('load');
    this.revealSelectedCategory();
  }

  revealSelectedCategory() {
    const slider = this.root.querySelector('[data-hb-sale-categories] .flickity-enabled');
    const selected = slider?.querySelector('[aria-current="true"]')?.closest('[data-subcollections-grid-item]');
    const next = slider?.querySelector('.flickity-prev-next-button.next');
    if (!selected || !next) return;
    // Use the theme's own controls so its active group and keyboard state stay in sync.
    const limit = slider.querySelectorAll('[data-subcollections-grid-item]').length;
    for (let step = 0; step < limit && !selected.classList.contains('is-selected') && !next.disabled; step++) next.click();
  }

  categoryUrl(href) {
    const url = new URL(location.href);
    url.pathname = new URL(href).pathname;
    url.searchParams.delete('page');
    url.searchParams.delete('hb_page');
    return url;
  }

  saveScrollPosition() {
    // A popstate has already changed location; never overwrite its destination state.
    if (this.renderedUrl !== location.href) return;
    const hbFiltersScroll = { url: location.href, x: window.scrollX, y: window.scrollY };
    history.replaceState({ ...history.state, hbFiltersScroll }, '', location.href);
  }

  restoreScrollPosition(position) {
    if (!position || position.url !== this.renderedUrl || !Number.isFinite(position.y)) return;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (location.href !== position.url || this.renderedUrl !== position.url) return;
      window.scrollTo({ left: position.x || 0, top: position.y, behavior: 'instant' });
    }));
  }

  onHistoryChange(state) {
    history.scrollRestoration = 'manual';
    if (location.href !== this.renderedUrl) {
      this.navigate(new URL(location.href), false, false, state?.hbFiltersScroll);
      return;
    }
    // The browser restored this document (including its grid). Keep it intact.
    this.request?.abort();
    this.root.removeAttribute('aria-busy');
    const status = this.root.querySelector('[data-hb-status]');
    status.hidden = true;
    status.textContent = '';
    this.restoreScrollPosition(state?.hbFiltersScroll);
  }

  submit() {
    const form = this.root.querySelector('[data-hb-form]');
    const url = new URL(form.action);
    url.search = new URLSearchParams(new FormData(form)).toString();
    // Explicitly retain the choice to show unavailable products across reloads.
    if (!url.searchParams.has('filter.v.availability')) url.searchParams.set('hb_all', '1');
    this.navigate(url);
  }

  async navigate(url, push = true, scrollToResults = false, savedPosition) {
    if (push) this.saveScrollPosition();
    this.countRequest?.abort();
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    this.root.setAttribute('aria-busy', 'true');
    const status = this.root.querySelector('[data-hb-status]');
    status.hidden = false;
    status.textContent = this.translations.loading;
    const openGroups = [...this.root.querySelectorAll('.hb-facet[open]')].map(details => details.dataset.group);
    const focused = document.activeElement;
    const focusName = focused?.getAttribute('name');
    const focusValue = focused?.value;
    const focusCategory = focused?.dataset?.hbCategoryLink;
    try {
      const endpoint = new URL(url);
      endpoint.searchParams.set('sections', this.root.dataset.sectionId);
      const response = await fetch(endpoint, { credentials: 'same-origin', signal: request.signal });
      if (!response.ok) throw new Error(`Section request: ${response.status}`);
      const sections = await response.json();
      const html = sections[this.root.dataset.sectionId];
      if (typeof html !== 'string') throw new Error('Shopify did not render the filter section');
      const page = new DOMParser().parseFromString(html, 'text/html');
      const next = page.querySelector('[data-hb-results]');
      if (!next) throw new Error('Missing filter results');
      if (request.signal.aborted) return;
      this.root.dispatchEvent(new CustomEvent('theme:rimg:unwatch', { bubbles: true }));
      this.updateThemeSection('unload');
      this.root.querySelector('[data-hb-results]').replaceWith(next);
      this.updateThemeSection('load');
      this.revealSelectedCategory();
      const searchBreadcrumbs = page.querySelector('[data-hb-search-breadcrumbs]');
      if (searchBreadcrumbs) document.querySelector('[data-hb-search-breadcrumbs]')?.replaceWith(searchBreadcrumbs);
      for (const details of next.querySelectorAll('.hb-facet')) details.open = openGroups.includes(details.dataset.group);
      if (focusName) [...next.querySelectorAll('[name]')].find(input => input.name === focusName && input.value === focusValue)?.focus({ preventScroll: true });
      if (focusCategory) [...next.querySelectorAll('[data-hb-category-link]')].find(link => link.dataset.hbCategoryLink === focusCategory)?.focus({ preventScroll: true });
      this.updateSwatches();
      this.updatePriceRanges();
      this.root.dispatchEvent(new CustomEvent('theme:rimg:watch', { bubbles: true }));
      if (url.href !== location.href) {
        push ? history.pushState(null, '', url) : history.replaceState(history.state, '', url);
      }
      this.renderedUrl = url.href;
      this.loadCategoryCounts();
      if (scrollToResults) {
        this.root.querySelector('.hb-toolbar')?.scrollIntoView({ block: 'start', behavior: 'instant' });
        this.root.querySelector('[data-hb-count]')?.focus({ preventScroll: true });
      }
      if (savedPosition) this.restoreScrollPosition(savedPosition);
      else this.saveScrollPosition();
    } catch (error) {
      if (request.signal.aborted) return;
      status.textContent = this.translations.error + ' ';
      const retry = document.createElement('a');
      retry.href = url;
      retry.textContent = this.translations.retry;
      status.append(retry);
      console.error('HB filters:', error);
    } finally {
      if (this.request === request) this.root.removeAttribute('aria-busy');
    }
  }

  updateThemeSection(action) {
    // Let the existing theme release/rebind cards and its subcollection slider.
    const section = this.root.closest('.shopify-section');
    if (!section || !section.querySelector('[data-section-type="static-collection"]')) return;
    section.dispatchEvent(new CustomEvent(`shopify:section:${action}`, {
      bubbles: true,
      detail: { sectionId: this.root.dataset.sectionId, hbFiltersUpdate: true }
    }));
  }

  updatePriceRanges() {
    for (const group of this.root.querySelectorAll('[data-group="price"]')) this.updatePriceRange(group);
  }

  updatePriceRange(group, source) {
    const minInput = group.querySelector('[data-hb-price-number="min"]');
    const maxInput = group.querySelector('[data-hb-price-number="max"]');
    const minRange = group.querySelector('[data-hb-price-bound="min"]');
    const maxRange = group.querySelector('[data-hb-price-bound="max"]');
    const limit = Number(maxInput.max);
    const clamp = value => Math.min(limit, Math.max(0, Number(value) || 0));
    let min = clamp(minInput.value);
    let max = maxInput.value === '' ? limit : clamp(maxInput.value);
    const bound = source?.dataset.hbPriceBound || source?.dataset.hbPriceNumber;
    if (source?.dataset.hbPriceBound) {
      if (bound === 'min') min = clamp(source.value);
      else max = clamp(source.value);
    }
    if (min > max) {
      if (bound === 'min') min = max;
      else max = min;
    }
    if (source) {
      minInput.value = min === 0 ? '' : String(min);
      maxInput.value = max === limit ? '' : String(max);
    }
    minRange.value = String(min);
    maxRange.value = String(max);
    const track = group.querySelector('[data-hb-price-range]');
    track.style.setProperty('--hb-price-min', `${limit ? min / limit * 100 : 0}%`);
    track.style.setProperty('--hb-price-max', `${limit ? max / limit * 100 : 100}%`);
    // Keep the lower handle reachable when both handles meet at the maximum.
    minRange.style.zIndex = min === limit ? '2' : '0';
  }

  updateSwatches() {
    const localized = new Map(Object.entries(this.translations.colors).map(([key, label]) => [label.toLowerCase(), key]));
    for (const swatch of this.root.querySelectorAll('[data-hb-swatch]')) {
      const value = swatch.dataset.hbSwatch.toLowerCase();
      const canonical = localized.get(value) || (Object.hasOwn(colorAliases, value) ? colorAliases[value] : value);
      swatch.style.backgroundColor = colors[canonical] || '#dedbd5';
    }
  }

  destroy() {
    history.scrollRestoration = this.previousScrollRestoration;
    this.request?.abort();
    this.countRequest?.abort();
    this.events.abort();
    this.root.dispatchEvent(new CustomEvent('theme:rimg:unwatch', { bubbles: true }));
  }
}

const instances = new WeakMap();
function initializeFilters(container) {
  const roots = container.matches?.('[data-hb-filters]') ? [container] : container.querySelectorAll('[data-hb-filters]');
  for (const root of roots) {
    if (!instances.has(root)) instances.set(root, new CollectionFilters(root));
  }
}
initializeFilters(document);
document.addEventListener('shopify:section:load', event => {
  if (!event.detail?.hbFiltersUpdate) initializeFilters(event.target);
});
document.addEventListener('shopify:section:unload', event => {
  if (event.detail?.hbFiltersUpdate) return;
  for (const root of event.target.querySelectorAll('[data-hb-filters]')) {
    instances.get(root)?.destroy();
    instances.delete(root);
  }
});
